import React, { useState, useMemo } from 'react';
import { ChevronRight, ChevronDown, Clock, DollarSign, Cpu, Activity } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';

/**
 * TraceHierarchyView - Nested span tree visualization
 * 
 * Features:
 * - Collapsible tree structure (3+ levels)
 * - Shows duration, cost, model, tokens
 * - Parent-child relationships via parent_id
 */
export const TraceHierarchyView = ({ spans = [] }) => {
  const [expandedNodes, setExpandedNodes] = useState(new Set());

  // Build tree structure from flat span list
  const spanTree = useMemo(() => {
    if (!spans || spans.length === 0) return [];

    // Create span lookup map
    const spanMap = {};
    spans.forEach(span => {
      spanMap[span.span_id] = { ...span, children: [] };
    });

    // Build parent-child relationships
    const roots = [];
    spans.forEach(span => {
      const node = spanMap[span.span_id];
      
      if (span.parent_span_id && spanMap[span.parent_span_id]) {
        // Add to parent's children
        spanMap[span.parent_span_id].children.push(node);
      } else {
        // Root node (no parent)
        roots.push(node);
      }
    });

    // Sort by timestamp
    const sortByTime = (nodes) => {
      nodes.sort((a, b) => {
        const timeA = new Date(a.ts_start || a.timestamp);
        const timeB = new Date(b.ts_start || b.timestamp);
        return timeA - timeB;
      });
      nodes.forEach(node => {
        if (node.children.length > 0) {
          sortByTime(node.children);
        }
      });
    };

    sortByTime(roots);
    return roots;
  }, [spans]);

  const toggleNode = (spanId) => {
    setExpandedNodes(prev => {
      const next = new Set(prev);
      if (next.has(spanId)) {
        next.delete(spanId);
      } else {
        next.add(spanId);
      }
      return next;
    });
  };

  const renderSpanNode = (span, depth = 0) => {
    const hasChildren = span.children && span.children.length > 0;
    const isExpanded = expandedNodes.has(span.span_id);
    const indentWidth = depth * 24;

    const duration = span.duration_ms || 0;
    const cost = span.cost_usd || span.cost_est_usd || 0;
    const model = span.model_id || 'unknown';
    const inputTokens = span.input_tokens || 0;
    const outputTokens = span.output_tokens || 0;
    const operation = span.operation || span.endpoint || 'operation';

    // Status color
    const statusColor = span.status === 'success' ? 'text-green-600' : 
                       span.status === 'error' ? 'text-red-600' : 
                       'text-yellow-600';

    return (
      <div key={span.span_id}>
        <div
          className={`
            flex items-center gap-3 py-2 px-3 hover:bg-gray-50 rounded cursor-pointer
            ${depth > 0 ? 'border-l-2 border-gray-200' : ''}
          `}
          style={{ marginLeft: `${indentWidth}px` }}
          onClick={() => hasChildren && toggleNode(span.span_id)}
        >
          {/* Expand/Collapse Icon */}
          <div className="w-5 h-5 flex-shrink-0">
            {hasChildren ? (
              isExpanded ? (
                <ChevronDown className="w-5 h-5 text-gray-600" />
              ) : (
                <ChevronRight className="w-5 h-5 text-gray-600" />
              )
            ) : (
              <div className="w-5 h-5" />
            )}
          </div>

          {/* Operation Name */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <Activity className={`w-4 h-4 ${statusColor}`} />
              <span className="font-medium text-sm truncate">
                {operation}
              </span>
              {hasChildren && (
                <span className="text-xs text-gray-500">
                  ({span.children.length} {span.children.length === 1 ? 'child' : 'children'})
                </span>
              )}
            </div>
          </div>

          {/* Metrics */}
          <div className="flex items-center gap-4 text-sm">
            {/* Duration */}
            <div className="flex items-center gap-1 text-gray-600">
              <Clock className="w-4 h-4" />
              <span>{duration}ms</span>
            </div>

            {/* Cost */}
            <div className="flex items-center gap-1 text-gray-600">
              <DollarSign className="w-4 h-4" />
              <span>${cost.toFixed(4)}</span>
            </div>

            {/* Model */}
            <div className="flex items-center gap-1 text-gray-600">
              <Cpu className="w-4 h-4" />
              <span className="text-xs">{model}</span>
            </div>

            {/* Tokens */}
            {(inputTokens > 0 || outputTokens > 0) && (
              <div className="text-xs text-gray-500">
                {inputTokens}→{outputTokens} tokens
              </div>
            )}
          </div>
        </div>

        {/* Render Children */}
        {hasChildren && isExpanded && (
          <div>
            {span.children.map(child => renderSpanNode(child, depth + 1))}
          </div>
        )}
      </div>
    );
  };

  if (!spans || spans.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Trace Hierarchy</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8 text-gray-500">
            No spans to display
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Activity className="w-5 h-5" />
          Trace Hierarchy
          <span className="text-sm font-normal text-gray-500">
            ({spans.length} {spans.length === 1 ? 'span' : 'spans'})
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        <div className="max-h-96 overflow-y-auto">
          {spanTree.map(rootSpan => renderSpanNode(rootSpan, 0))}
        </div>
      </CardContent>
    </Card>
  );
};

export default TraceHierarchyView;
